import React, { useState, useEffect } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Save, ArrowLeft, User, ShoppingBag, MapPin, CreditCard, Plus, X, Minus, Search } from "lucide-react";
import { adminApi } from "@/lib/api";
import { useAuthStore } from "@/lib/store";
import toast from "react-hot-toast";
import CustomDropdown from "@/components/admin/CustomDropdown";

export default function AdminOfflineSaleNew() {
  const navigate = useNavigate();
  const params = useParams();
  const { token } = useAuthStore();
  const saleId = params.id ? parseInt(params.id) : null;
  const isEdit = saleId !== null;
  
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [searchCustomer, setSearchCustomer] = useState("");
  const [searchProduct, setSearchProduct] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState<any | null>(null);
  const [orderItems, setOrderItems] = useState<Array<{product_id: number; product: any; quantity: number; price: number}>>([]);

  // Form state
  const [formData, setFormData] = useState({
    delivery_name: "",
    delivery_phone: "",
    delivery_address_line1: "",
    delivery_address_line2: "",
    delivery_city: "",
    delivery_state: "",
    delivery_pincode: "",
    delivery_country: "India",
    order_status: "pending",
    payment_status: "completed",
    payment_method: "Offline Sale",
    razorpay_payment_id: "",
    shipping: "0",
    tax: "0",
    discount: "0",
  });

  // Debounced search for customers (only when search >= 4 characters)
  useEffect(() => {
    if (!token) return;
    
    // Clear customers if search is less than 4 characters
    if (searchCustomer.length < 4) {
      setCustomers([]);
      return;
    }

    // Debounce the API call
    const timeoutId = setTimeout(async () => {
      try {
        const response = await adminApi.getUsers(token, { 
          limit: 50,
          search: searchCustomer.trim()
        });
        // Response is already unwrapped by adminApi.getUsers to { total, users }
        // But handle both wrapped and unwrapped formats
        const usersList = response.users || (response.data && response.data.users) || [];
        setCustomers(usersList);
      } catch (error: any) {
        console.error("Error fetching customers:", error);
        toast.error("Failed to search customers");
        setCustomers([]);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchCustomer, token]);

  // Debounced search for products (only when search >= 4 characters)
  useEffect(() => {
    if (!token) return;
    
    // Clear products if search is less than 4 characters
    if (searchProduct.length < 4) {
      setProducts([]);
      return;
    }

    // Debounce the API call
    const timeoutId = setTimeout(async () => {
      try {
        const response = await adminApi.getProducts(token, { 
          limit: 50,
          search: searchProduct.trim()
        });
        const productsList = response.products || response.data?.products || response.data || [];
        setProducts(productsList);
      } catch (error: any) {
        console.error("Error fetching products:", error);
        toast.error("Failed to search products");
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchProduct, token]);

  const handleAddProduct = (product: any) => {
    if (!product.stock || product.stock <= 0) {
      toast.error("Product is out of stock");
      return;
    }
    const existingItem = orderItems.find((item) => item.product_id === product.id);
    if (existingItem) {
      if (existingItem.quantity >= product.stock) {
        toast.error("Maximum stock available reached");
        return;
      }
      setOrderItems(
        orderItems.map((item) =>
          item.product_id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      );
    } else {
      const salePrice = product.sale_price || product.price || 0;
      setOrderItems([...orderItems, { product_id: product.id, product, quantity: 1, price: salePrice }]);
    }
    setSearchProduct("");
  };

  const handleRemoveProduct = (productId: number) => {
    setOrderItems(orderItems.filter((item) => item.product_id !== productId));
  };

  const handleUpdateQuantity = (productId: number, quantity: number) => {
    const item = orderItems.find((item) => item.product_id === productId);
    if (item && item.product) {
      if (quantity > item.product.stock) {
        toast.error(`Maximum stock available: ${item.product.stock}`);
        return;
      }
      if (quantity < 1) {
        handleRemoveProduct(productId);
        return;
      }
    }
    setOrderItems(
      orderItems.map((item) =>
        item.product_id === productId ? { ...item, quantity } : item
      )
    );
  };

  const handleUpdatePrice = (productId: number, price: number) => {
    if (price < 0) {
      toast.error("Price cannot be negative");
      return;
    }
    setOrderItems(
      orderItems.map((item) =>
        item.product_id === productId ? { ...item, price } : item
      )
    );
  };

  const calculateTotals = () => {
    const subtotal = orderItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const shipping = parseFloat(formData.shipping) || 0;
    const tax = parseFloat(formData.tax) || 0;
    const discount = parseFloat(formData.discount) || 0;
    const total = subtotal + shipping + tax - discount;
    return { subtotal, shipping, tax, discount, total };
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      toast.error("Authentication required");
      return;
    }

    if (!selectedCustomer) {
      toast.error("Please select a customer");
      return;
    }
    if (orderItems.length === 0) {
      toast.error("Please add at least one product");
      return;
    }
    if (!formData.delivery_name || !formData.delivery_phone || !formData.delivery_address_line1 || 
        !formData.delivery_city || !formData.delivery_state || !formData.delivery_pincode) {
      toast.error("Please fill in all required delivery address fields");
      return;
    }

    setLoading(true);

    try {
      const { subtotal, shipping, tax, discount, total } = calculateTotals();
      
      const orderData = {
        user_id: selectedCustomer.id,
        items: orderItems.map((item) => ({
          product_id: item.product_id,
          quantity: item.quantity,
          price: item.price,
        })),
        subtotal,
        shipping,
        tax,
        discount,
        total_amount: total,
        delivery_name: formData.delivery_name,
        delivery_phone: formData.delivery_phone,
        delivery_address_line1: formData.delivery_address_line1,
        delivery_address_line2: formData.delivery_address_line2 || undefined,
        delivery_city: formData.delivery_city,
        delivery_state: formData.delivery_state,
        delivery_pincode: formData.delivery_pincode,
        delivery_country: formData.delivery_country,
        order_status: formData.order_status,
        payment_status: formData.payment_status,
        payment_method: "Offline Sale", // Always set to "Offline Sale"
        razorpay_payment_id: formData.razorpay_payment_id || undefined,
      };

      const response = await adminApi.createOrder(orderData, token);
      const orderId = response.data?.id || response.id;

      if (!orderId) {
        throw new Error("Failed to create offline sale");
      }

      toast.success("Offline sale recorded successfully!");
      navigate(`/admin/offline-sales/${orderId}`);
    } catch (error: any) {
      console.error("Error creating offline sale:", error);
      toast.error(error.response?.data?.detail || "Failed to create offline sale");
    } finally {
      setLoading(false);
    }
  };

  // No need for client-side filtering - backend handles it
  const filteredCustomers = customers;
  const filteredProducts = products;

  return (
    <div className="p-4">
      {/* Header */}
      <div className="mb-4">
        <Link
          to="/admin/offline-sales"
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-aarsa-sage-600 mb-3 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Offline Sales
        </Link>
        <h1 className="text-xl font-bold font-playfair mb-0.5">{isEdit ? "Edit Offline Sale" : "Record New Offline Sale"}</h1>
        <p className="text-xs text-gray-500">Record an offline sale with explicit selling price</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="space-y-3">
          {/* Customer & Products Selection */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-aarsa-sage-50 to-aarsa-gold-50 border-b border-gray-200 px-4 py-2.5">
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 rounded-lg bg-gradient-to-br from-aarsa-sage-500 to-aarsa-gold-500">
                  <User className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-sm font-bold text-gray-900">Customer & Products</h2>
                <span className="text-xs text-red-500 ml-auto">* Required</span>
              </div>
            </div>
            <div className="p-4 space-y-4">
              {/* Customer Selection */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">
                  Select Customer <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Type at least 4 characters to search..."
                    value={searchCustomer}
                    onChange={(e) => setSearchCustomer(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                  />
                </div>
                {searchCustomer.length >= 4 && filteredCustomers.length > 0 && (
                  <div className="mt-2 border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                    {filteredCustomers.map((customer) => {
                      const displayName = `${customer.first_name || ""} ${customer.last_name || ""}`.trim() || "No name";
                      const displayEmail = customer.email || null;
                      const displayPhone = customer.phone || null;
                      
                      return (
                        <button
                          key={customer.id}
                          type="button"
                          onClick={() => {
                            setSelectedCustomer(customer);
                            setFormData({
                              ...formData,
                              delivery_name: displayName !== "No name" ? displayName : "",
                              delivery_phone: customer.phone?.replace(/^\+91/, "") || "",
                            });
                            setSearchCustomer("");
                          }}
                          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium text-gray-900">
                            {displayName}
                          </div>
                          <div className="text-xs text-gray-500">
                            {displayEmail && displayPhone 
                              ? `${displayEmail} • ${displayPhone}`
                              : displayEmail 
                              ? displayEmail
                              : displayPhone
                              ? displayPhone
                              : "No contact info"}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
                {selectedCustomer && (
                  <div className="mt-2 p-3 bg-aarsa-sage-50 rounded-lg">
                    <div className="font-medium text-sm text-gray-900">
                      {`${selectedCustomer.first_name || ""} ${selectedCustomer.last_name || ""}`.trim() || "No name"}
                    </div>
                    <div className="text-xs text-gray-600">
                      {selectedCustomer.email || selectedCustomer.phone || "No contact info"}
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedCustomer(null)}
                      className="mt-1 text-xs text-red-600 hover:text-red-700"
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>

              {/* Product Selection */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">
                  Add Products <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Type at least 4 characters to search..."
                    value={searchProduct}
                    onChange={(e) => setSearchProduct(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                  />
                </div>
                {searchProduct.length >= 4 && filteredProducts.length > 0 && (
                  <div className="mt-2 border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                    {filteredProducts.map((product) => (
                      <button
                        key={product.id}
                        type="button"
                        onClick={() => handleAddProduct(product)}
                        disabled={!product.stock || product.stock <= 0}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className="font-medium text-gray-900">{product.name}</div>
                        <div className="text-xs text-gray-500">
                          {product.product_code && <span className="font-mono">{product.product_code}</span>}
                          {product.product_code && " • "}
                          ₹{product.sale_price || product.price} | Stock: {product.stock || 0}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {orderItems.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {orderItems.map((item) => (
                      <div key={item.product_id} className="p-3 bg-gray-50 rounded-lg space-y-2">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="font-medium text-sm text-gray-900">{item.product.name}</div>
                            <div className="text-xs text-gray-500 mt-1">
                              Regular Price: ₹{item.product.sale_price || item.product.price || 0}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveProduct(item.product_id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">
                              Selling Price (₹) <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.price}
                              onChange={(e) => handleUpdatePrice(item.product_id, parseFloat(e.target.value) || 0)}
                              className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                              placeholder="0.00"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">Quantity</label>
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => handleUpdateQuantity(item.product_id, item.quantity - 1)}
                                className="w-6 h-6 flex items-center justify-center bg-white border border-gray-300 rounded hover:bg-gray-50"
                              >
                                <Minus className="w-3 h-3" />
                              </button>
                              <input
                                type="number"
                                min="1"
                                max={item.product.stock}
                                value={item.quantity}
                                onChange={(e) => handleUpdateQuantity(item.product_id, parseInt(e.target.value) || 1)}
                                className="w-12 px-1 py-1.5 text-sm text-center border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                              />
                              <button
                                type="button"
                                onClick={() => handleUpdateQuantity(item.product_id, item.quantity + 1)}
                                disabled={item.quantity >= item.product.stock}
                                className="w-6 h-6 flex items-center justify-center bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">Total</label>
                            <div className="px-2 py-1.5 text-sm font-semibold text-gray-900 bg-white border border-gray-200 rounded-lg">
                              ₹{(item.price * item.quantity).toFixed(2)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.div>

          {/* Row: Delivery Address + Order Totals */}
          <div className="grid grid-cols-2 gap-3">
            {/* Delivery Address */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
            >
              <div className="bg-gradient-to-r from-aarsa-sage-50 to-aarsa-gold-50 border-b border-gray-200 px-4 py-2.5">
                <div className="flex items-center gap-2.5">
                  <div className="p-1.5 rounded-lg bg-gradient-to-br from-aarsa-sage-500 to-aarsa-gold-500">
                    <MapPin className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-sm font-bold text-gray-900">Delivery Address</h2>
                  <span className="text-xs text-red-500 ml-auto">* Required</span>
                </div>
              </div>
              <div className="p-4 space-y-2.5">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="delivery_name"
                    value={formData.delivery_name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                    placeholder="Enter full name"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="delivery_phone"
                    value={formData.delivery_phone}
                    onChange={(e) => setFormData({ ...formData, delivery_phone: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                    required
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                    placeholder="Enter phone number"
                    maxLength={10}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Address Line 1 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="delivery_address_line1"
                    value={formData.delivery_address_line1}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                    placeholder="Enter address line 1"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Address Line 2</label>
                  <input
                    type="text"
                    name="delivery_address_line2"
                    value={formData.delivery_address_line2}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                    placeholder="Enter address line 2"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2.5">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      City <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="delivery_city"
                      value={formData.delivery_city}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                      placeholder="Enter city"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      State <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="delivery_state"
                      value={formData.delivery_state}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                      placeholder="Enter state"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2.5">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      Pincode <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="delivery_pincode"
                      value={formData.delivery_pincode}
                      onChange={(e) => setFormData({ ...formData, delivery_pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                      required
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                      placeholder="Enter pincode"
                      maxLength={6}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Country</label>
                    <input
                      type="text"
                      name="delivery_country"
                      value={formData.delivery_country}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                      placeholder="Enter country"
                    />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Order Totals */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
            >
              <div className="bg-gradient-to-r from-aarsa-sage-50 to-aarsa-gold-50 border-b border-gray-200 px-4 py-2.5">
                <div className="flex items-center gap-2.5">
                  <div className="p-1.5 rounded-lg bg-gradient-to-br from-aarsa-sage-500 to-aarsa-gold-500">
                    <ShoppingBag className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-sm font-bold text-gray-900">Order Totals</h2>
                </div>
              </div>
              <div className="p-4 space-y-2.5">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-medium">₹{calculateTotals().subtotal.toFixed(2)}</span>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Shipping (₹)</label>
                  <input
                    type="number"
                    name="shipping"
                    value={formData.shipping}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Tax (₹)</label>
                  <input
                    type="number"
                    name="tax"
                    value={formData.tax}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Discount (₹)</label>
                  <input
                    type="number"
                    name="discount"
                    value={formData.discount}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>
                <div className="border-t border-gray-300 pt-2 mt-2 flex justify-between">
                  <span className="font-semibold text-gray-900">Total:</span>
                  <span className="font-bold text-lg text-gray-900">₹{calculateTotals().total.toFixed(2)}</span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Order & Payment Status */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-aarsa-sage-50 to-aarsa-gold-50 border-b border-gray-200 px-4 py-2.5">
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 rounded-lg bg-gradient-to-br from-aarsa-sage-500 to-aarsa-gold-500">
                  <CreditCard className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-sm font-bold text-gray-900">Order & Payment Status</h2>
              </div>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Order Status</label>
                <CustomDropdown
                  value={formData.order_status}
                  onChange={(value) => setFormData({ ...formData, order_status: value })}
                  options={[
                    { value: "pending", label: "Pending" },
                    { value: "confirmed", label: "Confirmed" },
                    { value: "processing", label: "Processing" },
                    { value: "shipped", label: "Shipped" },
                    { value: "delivered", label: "Delivered" },
                  ]}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Payment Status</label>
                <CustomDropdown
                  value={formData.payment_status}
                  onChange={(value) => setFormData({ ...formData, payment_status: value })}
                  options={[
                    { value: "pending", label: "Pending" },
                    { value: "completed", label: "Completed" },
                  ]}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Payment Method</label>
                <input
                  type="text"
                  name="payment_method"
                  value={formData.payment_method}
                  readOnly
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-600"
                  placeholder="Offline Sale"
                />
                <p className="text-xs text-gray-500 mt-1">Payment method is automatically set to "Offline Sale"</p>
              </div>
            </div>
          </motion.div>

          {/* Submit Button */}
          <div className="flex justify-end gap-3 pt-2 pb-2">
            <Link
              to="/admin/offline-sales"
              className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-all"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 px-5 py-2 bg-gray-900 text-white text-sm font-semibold rounded-lg hover:bg-gray-800 transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Recording...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Record Offline Sale
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
